// CR109: Consolidated Batch Run Summary — compact stats + operations
// CR164: Merchants in-scope count derived from run config when available
import { useState, useEffect, useMemo } from "react";
import {
  Users,
  Database,
  FileText,
  GitMerge,
  CheckCircle2,
  XCircle,
  Store,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

export type RulePackSource = "run_override" | "resolved" | "missing" | "embedded_default";
export type DedupeMode = "deterministic" | "hybrid_ai_assist" | "ai_only";

interface BatchRunSummaryProps {
  run: {
    id: string;
    merchants_in_scope: number;
    total_staged_items: number;
    ever_staged_items?: number;
    ever_staged_fallback?: number;
    normalized_count: number;
    deduped_count: number;
    published_count: number;
    error_count: number;
    config_json: Record<string, unknown>;
  };
}

export function BatchRunSummary({ run }: BatchRunSummaryProps) {
  const [outputMerchantsCount, setOutputMerchantsCount] = useState<number>(0);
  const stagedCount = Math.max(
    run.ever_staged_items ?? 0,
    run.total_staged_items ?? 0,
    run.ever_staged_fallback ?? 0,
  );

  const merchantKeys = useMemo(() => {
    const config = run.config_json || {};
    const merchants = (config.merchants as string[]) || (config.merchant_ids as string[]) || [];
    return merchants;
  }, [run.config_json]);

  const selectedMerchantsCount = merchantKeys.length > 0 ? merchantKeys.length : run.merchants_in_scope;

  useEffect(() => {
    fetchOutputMerchantsCount();
  }, [run.id]);

  const fetchOutputMerchantsCount = async () => {
    try {
      const merchantKeysSet = new Set<string>();

      const { data: dedupedRows, error: dedupedError } = await supabase
        .from("cr075_staging_deals_deduped")
        .select("published_at, canonical_deal_json")
        .eq("batch_run_id", run.id);

      if (dedupedError) throw dedupedError;

      const publishedRows = (dedupedRows || []).filter((row: any) => row.published_at);
      for (const row of publishedRows) {
        const merchantKey = (row?.canonical_deal_json as Record<string, unknown>)?.merchant_key;
        if (typeof merchantKey === "string" && merchantKey.trim().length > 0) {
          merchantKeysSet.add(merchantKey.trim());
        }
      }

      if (merchantKeysSet.size > 0) {
        setOutputMerchantsCount(merchantKeysSet.size);
        return;
      }

      const { data: normalizedRows, error: normalizedError } = await supabase
        .from("cr075_staging_deals_normalized")
        .select("merchant_key")
        .eq("batch_run_id", run.id);

      if (normalizedError) throw normalizedError;

      for (const row of normalizedRows || []) {
        const merchantKey = row?.merchant_key;
        if (typeof merchantKey === "string" && merchantKey.trim().length > 0) {
          merchantKeysSet.add(merchantKey.trim());
        }
      }

      setOutputMerchantsCount(merchantKeysSet.size);
    } catch (err) {
      console.error("[BatchRunSummary] Failed to fetch output merchants count:", err);
      setOutputMerchantsCount(0);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-0 rounded-lg border bg-card px-1 py-1.5 text-sm">
        <StripStat icon={<Users className="h-3.5 w-3.5" />} label="Selected Merchants" value={selectedMerchantsCount} />
        <StripSeparator />
        <StripStat icon={<Store className="h-3.5 w-3.5" />} label="Merchants in Output" value={outputMerchantsCount} />
        <StripSeparator />
        <StripStat
          icon={<Database className="h-3.5 w-3.5" />}
          label="Staged"
          value={stagedCount}
          title="Highest single-attempt staged count observed for this run."
        />
        <StripSeparator />
        <StripStat icon={<FileText className="h-3.5 w-3.5" />} label="Normalized" value={run.normalized_count} />
        <StripSeparator />
        <StripStat icon={<GitMerge className="h-3.5 w-3.5" />} label="Deduped" value={run.deduped_count} />
        <StripSeparator />
        <StripStat icon={<CheckCircle2 className="h-3.5 w-3.5 text-primary" />} label="Published" value={run.published_count} highlight />
        <StripSeparator />
        <StripStat icon={<XCircle className="h-3.5 w-3.5" />} label="Errors" value={run.error_count} error={run.error_count > 0} />
      </div>
    </div>
  );
}

function StripStat({
  icon,
  label,
  value,
  highlight,
  error,
  title,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  highlight?: boolean;
  error?: boolean;
  title?: string;
}) {
  return (
    <div className="flex items-center gap-1.5 px-3 py-0.5" title={title}>
      {icon}
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className={`font-semibold tabular-nums ${error ? "text-destructive" : highlight ? "text-primary" : ""}`}>
        {value}
      </span>
    </div>
  );
}

function StripSeparator() {
  return <div className="w-px h-5 bg-border" />;
}
